// @flow

import { useDispatch } from 'react-redux';

import { selectTaxesDisplaySetting } from '@gandi/authentication';
import {
  selectCartCurrency,
  selectCartIsWorking,
  selectProductsByType,
} from '@gandi/cart';

import { notifyDanger } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';
import { Spinner } from '@gandi/ui';

import Counter from 'src/Overview/components/Products/components/components/Rows/Counter';
import { useAppSelector } from 'src/hooks';

import type { ProductSummary } from 'src/modules/mailbox-standalone/resources/products';

import { MAILBOX_SA_QUANTITY_MAX } from '../../../modules/constants';
import { useUpsertCartMailboxes } from '../../../modules/hooks/useUpsertCartMailboxes';
import Price from '../../Price';

import styles from './QuantityChoice.scss';

export function QuantityChoice({
  productType,
  product,
}: {
  product: ProductSummary,
  productType: { productType: string },
}): React.MixedElement {
  const dispatch = useDispatch();
  const { __ } = useTranslator();

  const isCartWorking = useAppSelector(selectCartIsWorking);

  const cartProducts = useAppSelector(state =>
    selectProductsByType(state, productType),
  );
  const cartProductItem = cartProducts?.find(
    item => item.payload.config_name === product.config_name,
  );

  const hasTaxes = !!cartProductItem?.total?.tax_amount;
  const displayTaxes = useAppSelector(selectTaxesDisplaySetting);
  const currency = useAppSelector(selectCartCurrency);

  const amount = displayTaxes
    ? cartProductItem?.computedTotalPrices?.afterTaxes
    : cartProductItem?.computedTotalPrices?.beforeTaxes;

  const { handleUpsertCartMailboxes } = useUpsertCartMailboxes();

  async function handleChange(value: number) {
    try {
      await handleUpsertCartMailboxes(
        {
          ...cartProductItem?.payload,
          quantity: value,
        },
        cartProductItem?.id,
      );
    } catch (err) {
      dispatch(
        notifyDanger({
          message:
            err.response?.data.message ||
            __('We are unable to add mailboxes to your cart.'),
        }),
      );
    }
  }

  return (
    <>
      {cartProductItem && (
        <div className={styles.quantityContainer}>
          <div className={styles.quantityCounter}>
            <p className={styles.quantityTitle}>
              {__('Number of mailboxes to create')}
            </p>
            <Counter
              key={cartProductItem.id}
              id={`count-${cartProductItem.id}`}
              count={cartProductItem?.payload?.quantity || 1}
              max={MAILBOX_SA_QUANTITY_MAX}
              onChange={handleChange}
              callbackDependencies={[
                cartProductItem?.payload?.autorenew,
                cartProductItem?.payload?.duration_month,
              ]}
              // we only need cart activity here, we want to allow changes while debouncing
              isLoading={isCartWorking}
            />
          </div>
          {isCartWorking || !Number.isFinite(amount) ? (
            <Spinner size="small" name="dots" />
          ) : (
            <Price
              currency={currency}
              price={amount}
              showExcludeVatMention={hasTaxes && !displayTaxes}
            />
          )}
        </div>
      )}
    </>
  );
}
